// packages/studio/web/src/editor/seedVfs.ts
//
// Fetches every workspace file (.arch sources, package.archspace manifests,
// .md documents) from a repo at a given ref and builds a
// Map<path, content> that can be handed to the LSP worker's virtual
// file system.
//
// API surface consumed (Plan 06):
//   GET /api/ws/:slug/r/:repo/tree/:ref   → { entries: [{name, kind, oid}] }
//   GET /api/ws/:slug/r/:repo/files/:ref/:path → { path, oid, content }
//
// The tree walk is RECURSIVE (`?recursive=1`), so `entry.name` is a full path
// and nested packages seed too — which is the normal monorepo shape. (It was
// once a flat root listing; the filter below reasons from the recursive form.)

import { isWorkspaceFile } from "@archlang/viewer-core/workspace-file";

export interface TreeEntry {
  readonly name: string;
  readonly kind: "blob" | "tree";
  readonly oid: string;
}

export interface TreePayload {
  readonly path: string;
  readonly entries: readonly TreeEntry[];
}

export interface FilePayload {
  readonly path: string;
  readonly oid: string;
  readonly content: string;
}

export interface SeedVfsArgs {
  readonly slug: string;
  readonly repo: string;
  readonly ref: string;
  /** Injected in tests; defaults to the global fetch. */
  readonly fetchFn?: typeof fetch;
  /** API origin prefix; defaults to API_BASE (empty in production/same-origin). */
  readonly apiBase?: string;
}

/**
 * Fetch all `.arch` blobs at the root of `repo@ref` and return a
 * `Map<path, content>` for the LSP worker's virtual file system.
 */
export async function seedVfs(args: SeedVfsArgs): Promise<Map<string, string>> {
  const { slug, repo, ref, fetchFn = fetch, apiBase = "" } = args;

  const base = `${apiBase}/api/ws/${encodeURIComponent(slug)}/r/${encodeURIComponent(repo)}`;

  // 1. Fetch the full recursive tree listing (flat full paths).
  const treeRes = await fetchFn(`${base}/tree/${encodeURIComponent(ref)}?recursive=1`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!treeRes.ok) {
    throw new Error(`tree fetch failed: ${treeRes.status}`);
  }
  const tree: TreePayload = await treeRes.json();

  // 2. Filter to model + document blobs (anywhere in the tree), by THE
  //    workspace-file law rather than a local re-statement of it. Both halves
  //    of that law were once re-stated wrongly here: `package.archspace` was
  //    missing, so a manifest committed from Studio VANISHED on the next load
  //    (package/space resolution silently fell back to the implicit root while
  //    the file sat there in the repo); and a byte-exact `.md` test dropped a
  //    `README.MD` the user had just created and committed, whose editor then
  //    opened an empty buffer over it.
  //
  //    `e.name` is a FULL PATH (the tree route is recursive), and the law reads
  //    the basename — an equality test would have seeded only a root manifest
  //    and dropped every nested one, which is the normal monorepo shape.
  const blobs = tree.entries.filter((e) => e.kind === "blob" && isWorkspaceFile(e.name));

  if (blobs.length === 0) {
    return new Map();
  }

  // 3. Fetch all blobs in parallel. Encode each path segment but keep the
  //    slashes so nested paths route to the wildcard /files handler.
  const results = await Promise.all(
    blobs.map(async (entry) => {
      const encodedPath = entry.name.split("/").map(encodeURIComponent).join("/");
      const fileRes = await fetchFn(`${base}/files/${encodeURIComponent(ref)}/${encodedPath}`, {
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      if (!fileRes.ok) {
        throw new Error(`file fetch failed for ${entry.name}: ${fileRes.status}`);
      }
      const payload: FilePayload = await fileRes.json();
      return [payload.path, payload.content] as const;
    }),
  );

  return new Map(results);
}
